import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import Button from '../button';
import Spinner from '../spinner';
import GoogleSignInButton from '../google-sign-in-button';
import MicrosoftSignInButton from '../microsoft-sign-in-button';
import useAuth from '~/modules/hooks/use-auth';
import GoogleIdentityService from '~/modules/services/google-identity-service';
import MicrosoftIdentityService from '~/modules/services/microsoft-identity-service';

const LOGIN_FORM = 'login-form';
const FORGOT_PASSWORD_FORM = 'forgot-password-form';

function AuthModal() {
	let [email, setEmail] = useState('');
	let [password, setPassword] = useState('');
	let [form, setForm] = useState(LOGIN_FORM);
	let [isLoginValid, setIsLoginValid] = useState(false);

	const {
		isLoggedIn,
		isLoggingIn,
		login,
		isResettingPassword,
		lastAttemptedAt,
		loginError,
		resetPasswordError,
		resetPasswordSuccess,
		isTwoFactorLogin,
		requestResetPassword,
		clearResetPasswordFeedback,
		googleLogin,
		isGoogleLoggingIn,
		microsoftLogin,
		isMicrosoftLoggingIn,
		ssoLoginError
	} = useAuth();

	// One lock for every way into the modal. A provider's own `waiting` state is local to its
	// button, and the mutation flags only go true once a popup has *returned* a code — so
	// without this, a second sign-in (or a password login) could be started while a popup is
	// open, and whichever session mutation answered last would overwrite the stored one.
	let [ssoBusy, setSsoBusy] = useState(false);

	const signingIn = ssoBusy || isGoogleLoggingIn || isMicrosoftLoggingIn || isLoggingIn;

	// Whether to draw the "or" divider and the row at all. The buttons each decide for
	// themselves whether they can render, so without this a deployment with no provider
	// configured — or the moment before the boot `system-check` request lands — shows an
	// orphan divider above an empty row where previously it showed only the password form.
	let [hasProvider, setHasProvider] = useState(false);

	useEffect(() => {
		let cancelled = false;

		Promise.all([GoogleIdentityService.clientId(), MicrosoftIdentityService.clientId()])
			.then(([google, microsoft]) => {
				if(!cancelled && (google || microsoft)) {
					setHasProvider(true);
				}
			})
			.catch(() => { /* neither configured, or the request failed: offer neither */ });

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		let isFormValid = email !== '' && password !== '';
		setIsLoginValid(isFormValid);
	});

	const handleEmailChange = (e) => {
		setEmail(e.target.value);
	};

	const handlePasswordChange = (e) => {
		setPassword(e.target.value);
	};

	const handleLogin = (e) => {
		e.preventDefault();

		// The button is disabled while a sign-in is in flight, but Enter in the form would
		// still submit it.
		if(signingIn) {
			return;
		}

		// login is mutateAsync: onError in use-auth already owns the failure UX, so swallow
		// the rejection here or every failed login also reports as an unhandled rejection.
		login({ username: email.trim(), password }).catch(() => { /* handled by onError */ });

		// Remove password after login attempt (success or failure)
		setPassword('');
	};

	// onError in use-auth owns the failure UX in both cases, so the rejections are swallowed
	// here or a refused sign-in also reports as an unhandled rejection.
	const handleGoogleCode = (code) => {
		if(!code) {
			return;
		}

		googleLogin({ code }).catch(() => { /* handled by onError */ });
	};

	const handleMicrosoftCode = ({ code, redirectUri, codeVerifier } = {}) => {
		if(!code) {
			return;
		}

		microsoftLogin({ code, redirectUri, codeVerifier }).catch(() => { /* handled by onError */ });
	};

	const handlePasswordReset = (e) => {
		e.preventDefault();

		const url = `${window.location.origin}/reset/:token`;
		requestResetPassword({ email, url });
	};

	const handleAutoFill = (e) => {
		let autofill = e.animationName === 'onAutoFillStart';
		if( autofill ) {
			let isFormValid = email !== '' && password !== '';
			setIsLoginValid(isFormValid);
		}
	};

	const shouldBeOpen = () => {
		return !!(!isLoggedIn && lastAttemptedAt);
	};

	const showForgotPasswordForm = () => {
		setForm(FORGOT_PASSWORD_FORM);
	};

	const showLoginForm = () => {
		clearResetPasswordFeedback();

		setForm(LOGIN_FORM);
	};

	const canRequestPasswordReset = () => {
		let isFormValid = email;

		return !isResettingPassword && isFormValid;
	};

	const renderLoginError = () => {
		if( !loginError ) {
			return null;
		}

		return (
			<div className="px-auth-modal-error" data-cy="login-error">
				Invalid username or password
			</div>
		);
	};

	const renderSsoError = () => {
		if( !ssoLoginError ) {
			return null;
		}

		return (
			<div className="px-auth-modal-error" data-cy="sso-login-error">
				{ ssoLoginError.message || 'Could not sign in' }
			</div>
		);
	};

	const renderTwoFactorLogin = () => {
		if( !isTwoFactorLogin ) {
			return null;
		}

		return (
			<div className="px-auth-modal-success" data-cy="login-success">
				Two factor authentication enabled. Please check your email for a verification link.
			</div>
		);
	};

	const renderResetPasswordError = () => {
		if( !resetPasswordError ) {
			return null;
		}

		return (
			<div className="px-auth-modal-error" data-cy="password-reset-error">
				Failed to reset password
			</div>
		);
	};

	const renderResetPasswordSuccess = () => {
		if( !resetPasswordSuccess ) {
			return null;
		}

		return (
			<div className="px-auth-modal-success" data-cy="password-reset-success">
				Please check your email for a password reset link
			</div>
		);
	};

	const renderForm = () => {
		return form === FORGOT_PASSWORD_FORM
			? renderForgotPasswordForm()
			: renderLoginForm();
	};

	const renderLoginForm = () => {
		return (
			<form name="loginForm" onSubmit={handleLogin}>
				<div className="px-auth-modal-header">
					<h3 className="px-auth-modal-header-title">Log In</h3>
				</div>

				<div className="px-auth-modal-body">
					{ renderLoginError() }
					{ renderSsoError() }
					{ renderTwoFactorLogin() }

					<input
						id="loginEmail"
						type="text"
						placeholder="Email Address"
						value={email}
						onChange={handleEmailChange}
						onAnimationStart={handleAutoFill}
						data-cy="login-email"
						autoFocus
					/>

					<input
						id="loginPassword"
						type="password"
						placeholder="Password"
						value={password}
						onChange={handlePasswordChange}
						onAnimationStart={handleAutoFill}
						data-cy="login-password"
					/>
				</div>

				<div className="px-auth-modal-footer">
					<p className="px-auth-modal-footer-forgot">
						<a
							href="#"
							onClick={showForgotPasswordForm}
							data-cy="password-reset-nav"
						>
							Forgot Password?
						</a>
					</p>

					{ !isLoggingIn
						? (
							<Button
								type="submit"
								className="px-auth-modal-footer-btn"
								onClick={handleLogin}
								disabled={!isLoginValid || signingIn}
								data-cy="login-btn"
							>
							Log In
							</Button>
						)
						: (
							<Spinner small></Spinner>
						)}
				</div>

				{ hasProvider && (
					<div className="px-auth-modal-sso">
						<div className="px-auth-modal-divider"><span>or</span></div>
						<div className="px-auth-modal-sso-buttons">
							<GoogleSignInButton
								onCode={handleGoogleCode}
								onBusyChange={setSsoBusy}
								disabled={signingIn}
							/>
							<MicrosoftSignInButton
								onCode={handleMicrosoftCode}
								onBusyChange={setSsoBusy}
								disabled={signingIn}
							/>
						</div>
					</div>
				) }
			</form>
		);
	};

	const renderForgotPasswordForm = () => {
		return (
			<form name="forgotPasswordForm">
				<div className="px-auth-modal-header">
					<h3 className="px-auth-modal-header-title">Forgot Password</h3>
				</div>

				<div className="px-auth-modal-body">
					{ renderResetPasswordSuccess() }
					{ renderResetPasswordError() }

					<input
						type="text"
						placeholder="Email Address"
						value={email}
						onChange={handleEmailChange}
						data-cy="password-reset-email"
					/>
				</div>

				<div className="px-auth-modal-footer">
					<p className="px-auth-modal-footer-forgot">
						<a
							href="#"
							onClick={showLoginForm}
							data-cy="login-nav"
						>
							Login
						</a>
					</p>

					{!isResettingPassword
						? (
							<Button
								type="submit"
								className="px-auth-modal-footer-btn"
								onClick={handlePasswordReset}
								disabled={!canRequestPasswordReset()}
								data-cy="password-reset-btn"
							>
							Reset Password
							</Button>
						)
						: (
							<Spinner small></Spinner>
						)}
				</div>
			</form>
		);
	};

	return (
		<div>
			<ReactModal
				isOpen={shouldBeOpen()}
				contentLabel="Pixwel Log In"
				className="px-auth-modal"
				overlayClassName="px-auth-overlay"
				bodyOpenClassName="px-body-auth-modal-open"
				htmlOpenClassName="px-html-auth-modal-open"
			>
				{ renderForm() }
			</ReactModal>
		</div>
	);
}

export default AuthModal;
